import * as React from 'react';
import { translate } from 'react-i18next';
import { InjectedRouter } from 'react-router';
const { connect } = require('react-redux');
import { Field, reduxForm, getFormSyncErrors } from 'redux-form';
import { IStore } from 'redux/IStore';
import * as c from 'models/constants';
import { PortalPageSection, ButtonsSection, PageTitle } from 'components/PortalStructure';
import { Button, ErrorSummary, LoadingOverlayWrapper, WhiteButton } from 'components';
import { PortalSelectMenu, PortalInputField, PortalSwitchField } from 'components/FormControls';
import {
	required,
	maxLength,
	isNumber,
	minValue,
	isWholeNumber,
	priceFieldNormalization,
	aboveZero,
} from 'helpers/validationattributes';
import { preventRouterLeave } from 'helpers/hooks';
import { IManageShopShippingOption, IManageShopShippingOptionAction } from 'models/shop/manageshopshippingoption';
import { IShopShippingOption } from 'models/shop/shopshippingoption';
import { IDomainValue } from 'models/common';
import { IModalAction } from 'models/modal';
import { showModal, closeModal } from 'modules/modal';
import {
	getShippingOption,
	submitCreateShippingOption,
	submitUpdateShippingoption,
	deleteShippingOption,
	clearErrorMessage,
} from 'modules/shop/manageshopshippingoption';
import { ManageShopWrapper } from 'containers';
import { getCountryDomainValues } from 'redux/modules/domainvalues';
import { IDomainValuesAction } from 'models/domainvalues/domainvalues';

interface IProps {
	manageShopShippingOption?: IManageShopShippingOption;
	currentFormValues: IShopShippingOption;
	initialValues: IShopShippingOption;
	params?: IParams;
	location: any;
	router: InjectedRouter;
	route: any;
	countryDomainValues: IDomainValue[];

	/* form props */
	pristine: boolean;
	dirty: boolean;
	submitting: boolean;
	handleSubmit: any;
	submitFailed: boolean;
	formErrors?: any;

	/* functions props */
	getShippingOption: (id: string) => IManageShopShippingOptionAction;
	submitCreateShippingOption: (option: IShopShippingOption) => IManageShopShippingOptionAction;
	submitUpdateShippingoption: (option: IShopShippingOption) => IManageShopShippingOptionAction;
	deleteShippingOption: (shippingOptionId: string, shopId: string) => IManageShopShippingOptionAction;
	clearErrorMessage: () => IManageShopShippingOptionAction;
	t?: (key: string) => string;
	showModal: (modalType: string, modalProps: any) => IModalAction;
	closeModal: () => IModalAction;
	onSubmitShippingOption?: () => void;
	getCountryDomainValues?: () => IDomainValuesAction;
}

interface IParams {
	id?: string;
	shippingOptionId?: string;
}

interface IState {
	priceFieldCursor: number; // For keeping the current field position
}

const maxLength255 = maxLength(255);
const maxLength4000 = maxLength(4000);
const minValue0 = minValue(0);

@preventRouterLeave()
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ManageShopShippingOptionForm extends React.Component<IProps, IState, any> {
	constructor(props) {
		super(props);
		this.priceFieldRef = React.createRef();
		this.state = {
			priceFieldCursor: null,
		};
		this.onSubmit = this.onSubmit.bind(this);
		this.deleteShippingOptionClick = this.deleteShippingOptionClick.bind(this);
		this.openDeleteShippingOption = this.openDeleteShippingOption.bind(this);
	}

	private priceFieldRef;

	public componentDidMount() {
		this.props.getCountryDomainValues();
		this.props.getShippingOption(this.props.params.shippingOptionId);
	}

	public componentWillUnmount() {
		this.props.clearErrorMessage();
	}

	public onSubmit(form: IShopShippingOption) {
		const availableToArray = form.availableTo as any[];

		if (availableToArray.find((x) => x === c.WORLDWIDE_OPTION_KEY)) {
			form.availableTo = this.props.countryDomainValues.map((x) => x.key).join(',');
		} else if (availableToArray.find((x) => x === c.EUROPEAN_UNION_OPTION_KEY)) {
			const europeanUnionCountryKeys = c.EUROPEAN_UNION_COUNTRY_KEYS;
			const available = Array.from(europeanUnionCountryKeys);
			availableToArray.forEach((x) => {
				if (x !== c.EUROPEAN_UNION_OPTION_KEY && !available.find((y) => y === x)) {
					available.push(x);
				}
			});
			form.availableTo = available.join(',');
		} else {
			form.availableTo = form.availableTo.join(',');
		}

		if (this.props.manageShopShippingOption.isEditMode) {
			this.props.submitUpdateShippingoption(form);
		} else {
			form.shopId = this.props.params.id;
			this.props.submitCreateShippingOption(form);
		}
	}

	private openDeleteShippingOption() {
		const { t } = this.props;

		const dialogContent = (
			<div>
				<img src={require('containers/Html/icons/icon_notification.png')} />
				<h2>{t('BSPortal_Common_ConfirmationDialog_Sure')}</h2>
				<p>{t('Shop_ManageShop_ShippingOption_AboutDelete')}</p>
				<p>{t('BSPortal_Common_ConfirmationDialog_Confirm')}</p>
			</div>
		);

		this.props.showModal('ConfirmationDialog', {
			confirmAction: this.deleteShippingOptionClick,
			yesButtonText: t('BSPortal_Common_YesDelete'),
			noButtonText: t('BSPortal_Common_NoWait'),
			content: dialogContent,
			closeModal: this.props.closeModal,
		});
	}

	private deleteShippingOptionClick(confirm: boolean) {
		if (confirm) {
			this.props.deleteShippingOption(this.props.params.shippingOptionId, this.props.params.id);
		}
		this.props.closeModal();
	}

	private mapErrorKeyToMessage(key: string, t: (key: string) => string) {
		switch (key) {
			case 'name':
				return t('Shop_ManageShop_ShippingOption_Label_Title');

			case 'availableTo':
				return t('Shop_ManageShop_ShippingOption_Label_OptionsAvailableTo');

			case 'additionalInformation':
				return t('Shop_ManageShop_ShippingOption_Label_AdditionalInformation');

			default:
				return key;
		}
	}

	public render() {
		const { handleSubmit, t, formErrors, submitFailed, submitting, pristine, location, countryDomainValues } =
			this.props;

		const errorSummaryProps = {
			formErrors,
			submitFailed,
			error: this.props.manageShopShippingOption ? this.props.manageShopShippingOption.error : false,
			message: this.props.manageShopShippingOption ? this.props.manageShopShippingOption.message : null,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
		};

		const countries = [];
		countries.push({ key: c.WORLDWIDE_OPTION_KEY, value: t(`DV_Countries_${c.WORLDWIDE_OPTION_KEY}`) });
		countries.push({ key: c.EUROPEAN_UNION_OPTION_KEY, value: t(`DV_Countries_${c.EUROPEAN_UNION_OPTION_KEY}`) });
		if (countryDomainValues) {
			countryDomainValues.forEach((country: IDomainValue) => {
				countries.push({
					key: country.key,
					value: t(`DV_Countries_${country.key}`),
				});
			});

			countries.sort(function (a: IDomainValue, b: IDomainValue) {
				if (a.value < b.value) {
					return -1;
				}
				if (a.value > b.value) {
					return 1;
				}
				return 0;
			});
		}

		return (
			<ManageShopWrapper id={this.props.params.id}>
				<div className="shopShippingOptionsFormWrapper">
					<PageTitle title={t('Shop_ManageShop_ShippingOption_Label_ShippingOption')} />
					<LoadingOverlayWrapper
						active={this.props.manageShopShippingOption.isFetching}
						spinner={true}
						text={t('BSPortal_Common_Loading')}
						background="rgba(0, 0, 0, 0.1)"
					>
						<form onSubmit={handleSubmit(this.onSubmit)}>
							<Field name="id" component="input" type="hidden" />
							<div className="profileError">
								<ErrorSummary {...errorSummaryProps} />
							</div>
							<PortalPageSection title={t('Shop_ManageShop_ShippingOption_Label_ShippingRate')}>
								<div className="row">
									<div className="col-md-3">
										<Field
											name="price"
											component={PortalInputField}
											type="text"
											label={t('Shop_ManageShop_ShippingOption_Label_Price')}
											validate={[required, isNumber, minValue0]}
											customRef={this.priceFieldRef}
											customCursor={this.state.priceFieldCursor}
											onChangeAction={(value) => {
												this.setState({ priceFieldCursor: value.target.selectionStart });
												const decimalValue = priceFieldNormalization(value.target.value);
												return (value.target.value = decimalValue);
											}}
											min={0}
											step={0.01}
											inputMode={'decimal'}
											normalize={aboveZero}
										/>
									</div>
								</div>
							</PortalPageSection>
							<PortalPageSection title={t('Shop_ManageShop_ShippingOption_Label_DeliveryDestinations')}>
								<div className="row">
									<div className="col-md-12">
										<Field
											name="availableTo"
											component={PortalSelectMenu}
											items={countries}
											validate={[required, maxLength4000]}
											isRequired={true}
											multi={true}
											closeOnSelect={false}
											label={t('Shop_ManageShop_ShippingOption_Label_OptionsAvailableTo')}
										/>
									</div>
								</div>
							</PortalPageSection>
							<PortalPageSection title={t('Shop_ManageShop_ShippingOption_Label_Details')}>
								<div className="row">
									<div className="col-md-6">
										<Field
											name="name"
											component={PortalInputField}
											type="text"
											label={t('Shop_ManageShop_ShippingOption_Label_Title')}
											validate={[required, maxLength255]}
										/>
									</div>
									<div className="col-md-3">
										<Field
											name="daysMin"
											component={PortalInputField}
											type="text"
											inputMode="numeric"
											label={t('Shop_ManageShop_ShippingOption_Label_DeliveryTimeInDays')}
											validate={[required, minValue0, isWholeNumber]}
										/>
									</div>
									<div className="col-md-3">
										<Field
											name="daysMax"
											component={PortalInputField}
											type="text"
											inputMode="numeric"
											validate={[required, minValue0, isWholeNumber]}
										/>
									</div>
								</div>

								<div className="row">
									<div className="col-md-12">
										<Field
											name="additionalInformation"
											component={PortalInputField}
											type="text"
											label={t('Shop_ManageShop_ShippingOption_Label_AdditionalInformation')}
											description={t('Shop_ManageShop_ShippingOption_AdditionalInformation_Desc')}
											validate={[maxLength4000]}
										/>
									</div>
								</div>
							</PortalPageSection>
							<PortalPageSection title={t('Shop_ManageShop_ShippingOption_Label_SpecialOffers')}>
								<div className="row">
									<div className="col-md-6">
										<Field
											name="freeShipping"
											component={PortalSwitchField}
											type="checkbox"
											label={t('Shop_ManageShop_ShippingOption_Label_OfferFreeShipping')}
										/>
									</div>
								</div>

								<div className="row">
									<div className="col-md-6">
										<Field
											name="freeShippingMinAmount"
											component={PortalInputField}
											type="text"
											inputMode="decimal"
											label={t('Shop_ManageShop_ShippingOption_Label_FreeShippingMinAmount')}
											validate={[isNumber, minValue0]}
										/>
									</div>
								</div>
							</PortalPageSection>
							<ButtonsSection>
								<div className="row">
									<div className="col-sm-6" />
									<div className="col-sm-6">
										<div className="floatRight">
											<WhiteButton type="button" onClick={this.openDeleteShippingOption}>
												{t('BSPortal_Common_Delete')}
											</WhiteButton>
											<Button type="submit" disabled={pristine || submitting}>
												{t('BSPortal_Common_SaveChanges')}
											</Button>
											<div className="clear" />
										</div>
										<div className="clear" />
									</div>
								</div>
							</ButtonsSection>
						</form>
					</LoadingOverlayWrapper>
				</div>
			</ManageShopWrapper>
		);
	}
}

let ManageShopShippingOption = reduxForm({
	form: c.FORM_NAME_MANAGE_SHOP_SHIPPING_OPTION,
	enableReinitialize: true,
})(ManageShopShippingOptionForm);

ManageShopShippingOption = connect(
	(state: IStore) => {
		const shippingOption: IShopShippingOption = state.manageShopShippingOption.shopShippingOption;

		if (
			shippingOption.availableTo &&
			shippingOption.availableTo.length > 0 &&
			!Array.isArray(shippingOption.availableTo)
		) {
			const splitted = shippingOption.availableTo.split(',');
			shippingOption.availableTo = splitted.map((option) => {
				return parseInt(option, 10);
			});

			let available: any[] = shippingOption.availableTo;
			const europeanUnionCountryKeys = c.EUROPEAN_UNION_COUNTRY_KEYS;
			let allCountryKeys = [];
			if (state.domainValues && state.domainValues.countries) {
				allCountryKeys = state.domainValues.countries.map((x) => x.key);
			}

			if (allCountryKeys.every((x) => shippingOption.availableTo.indexOf(x) >= 0)) {
				available = [c.WORLDWIDE_OPTION_KEY];
			} else if (europeanUnionCountryKeys.every((x) => shippingOption.availableTo.indexOf(x) >= 0)) {
				available = [c.EUROPEAN_UNION_OPTION_KEY];
				shippingOption.availableTo.forEach((x) => {
					if (!europeanUnionCountryKeys.find((y) => y === x)) {
						available.push(x);
					}
				});
			}

			shippingOption.availableTo = available;
		}

		let currentFormValues: IShopShippingOption = null;
		if (
			state.form[c.FORM_NAME_MANAGE_SHOP_SHIPPING_OPTION] &&
			state.form[c.FORM_NAME_MANAGE_SHOP_SHIPPING_OPTION].values
		) {
			currentFormValues = state.form[c.FORM_NAME_MANAGE_SHOP_SHIPPING_OPTION].values;
		} else {
			currentFormValues = shippingOption;
		}

		return {
			initialValues: state.manageShopShippingOption.shopShippingOption,
			manageShopShippingOption: state.manageShopShippingOption,
			currentFormValues,
			formErrors: getFormSyncErrors(c.FORM_NAME_MANAGE_SHOP_SHIPPING_OPTION)(state),
			countryDomainValues: state.domainValues.countries,
		};
	},
	(dispatch) => ({
		getShippingOption: (id: string) => dispatch(getShippingOption(id)),
		submitCreateShippingOption: (option: IShopShippingOption) => dispatch(submitCreateShippingOption(option)),
		submitUpdateShippingoption: (option: IShopShippingOption) => dispatch(submitUpdateShippingoption(option)),
		deleteShippingOption: (shippingOptionId: string, shopId: string) =>
			dispatch(deleteShippingOption(shippingOptionId, shopId)),
		clearErrorMessage: () => dispatch(clearErrorMessage()),
		closeModal: () => dispatch(closeModal()),
		showModal: (modalType: string, modalProps: any) => dispatch(showModal(modalType, modalProps)),
		getCountryDomainValues: () => dispatch(getCountryDomainValues()),
	}),
)(ManageShopShippingOption);

export { ManageShopShippingOption };
